"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PaginationBase = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _designTokensReact = require("@smartsheet/design-tokens-react");
var _lodestarIcons = require("@smartsheet/lodestar-icons");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _sdsThemeInternal = require("@smartsheet/sds-theme-internal");
var _ursaIcons = require("@smartsheet/ursa-icons");
var _react = _interopRequireWildcard(require("react"));
var _i18n = require("../../../i18n/i18n");
var _useTheme = require("../../../theme/utils/useTheme");
var _liveregion = require("../../liveregion");
var _paginationTheme = require("../paginationTheme");
var _styled = require("../styled");
var _StyledIconButton = require("../styled/StyledIconButton");
var _StyledTypeRamp = require("../styled/StyledTypeRamp");
const PaginationBase = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let {
    className,
    clientId,
    entitiesPerPage,
    from,
    includeFirstAndLastPage = false,
    onFirstPageClick,
    onLastPageClick,
    onNextClick,
    onPrevClick,
    total,
    variant = "inline",
    ...rest
  } = _ref;
  if (process.env.NODE_ENV !== "production") {
    if (entitiesPerPage < 1) {
      console.warn("Pagination: `entitiesPerPage` cannot be less than 1.");
    }
    if (total < 1) {
      console.warn(`Pagination: \`total\` (${total}) cannot be less than 1.`);
    }
    if (from < 1) {
      console.warn(`Pagination: \`from\` (${from}) cannot be less than 1.`);
    }
  }
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const {
    i18n
  } = (0, _i18n.useI18n)();
  const cannotGoBack = from === 1;
  const cannotGoForward = from + entitiesPerPage > total;
  const to = Math.min(from + entitiesPerPage - 1, total);
  const {
    getLiveRegionProps,
    speak
  } = (0, _liveregion.useLiveRegion)();
  const icons = (0, _sdsThemeInternal.themed)({
    firstPage: {
      "lodestar-light": /*#__PURE__*/_react.default.createElement(_lodestarIcons.PaginationFirstPageIcon, {
        color: cannotGoBack ? (0, _designTokensReact.color)("color--icons--neutral--strong") : (0, _designTokensReact.color)("color--text--neutral--strong")
      }),
      "ursa-light": /*#__PURE__*/_react.default.createElement(_ursaIcons.ChevronLeftLineIcon, {
        size: 20,
        color: cannotGoBack ? (0, _designTokensReact.color)("color--icons--neutral--weaker") : (0, _designTokensReact.color)("color--text--neutral--strong")
      })
    },
    previousPage: {
      "lodestar-light": /*#__PURE__*/_react.default.createElement(_lodestarIcons.PaginationArrowLeftIcon, {
        color: cannotGoBack ? (0, _designTokensReact.color)("color--icons--neutral--strong") : (0, _designTokensReact.color)("color--text--neutral--strong")
      }),
      "ursa-light": /*#__PURE__*/_react.default.createElement(_ursaIcons.ChevronLeftIcon, {
        size: 20,
        color: cannotGoBack ? (0, _designTokensReact.color)("color--icons--neutral--weaker") : (0, _designTokensReact.color)("color--text--neutral--strong")
      })
    },
    nextPage: {
      "lodestar-light": /*#__PURE__*/_react.default.createElement(_lodestarIcons.PaginationArrowRightIcon, {
        color: cannotGoForward ? (0, _designTokensReact.color)("color--icons--neutral--strong") : (0, _designTokensReact.color)("color--text--neutral--strong")
      }),
      "ursa-light": /*#__PURE__*/_react.default.createElement(_ursaIcons.ChevronRightIcon, {
        size: 20,
        color: cannotGoForward ? (0, _designTokensReact.color)("color--icons--neutral--weaker") : (0, _designTokensReact.color)("color--text--neutral--strong")
      })
    },
    lastPage: {
      "lodestar-light": /*#__PURE__*/_react.default.createElement(_lodestarIcons.PaginationLastPageIcon, {
        color: cannotGoForward ? (0, _designTokensReact.color)("color--icons--neutral--strong") : (0, _designTokensReact.color)("color--text--neutral--strong")
      }),
      "ursa-light": /*#__PURE__*/_react.default.createElement(_ursaIcons.ChevronRightLineIcon, {
        size: 20,
        color: cannotGoForward ? (0, _designTokensReact.color)("color--icons--neutral--weaker") : (0, _designTokensReact.color)("color--text--neutral--strong")
      })
    }
  });
  (0, _react.useEffect)(() => {
    speak(i18n._( /* i18n */{
      comment: "This string is read to a screen reader user using a live region when the pagination content changes",
      id: "{from} through {to} of {total}",
      values: {
        from,
        to,
        total
      }
    }));
  }, [speak, from, i18n, to, total]);
  return /*#__PURE__*/_react.default.createElement(_styled.Container, (0, _extends2.default)({
    orientation: "row",
    space: _paginationTheme.paginationTheme.container.space(themeMode),
    clientId: clientId,
    ref: ref,
    variant: variant,
    className: className,
    role: "group",
    tabIndex: 0,
    "aria-label": i18n._( /* i18n */{
      comment: "This aria label is read to the user when the screen reader focuses on the container of the pagination component",
      id: "Pagination, showing {from} through {to} of {total} entities",
      values: {
        from,
        to,
        total
      }
    }),
    themeMode: themeMode
  }, rest), includeFirstAndLastPage ? /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_StyledIconButton.StyledIconButton, {
    appearance: _paginationTheme.paginationTheme.iconButton.appearance(themeMode),
    clientId: (0, _lodestarUtils.getClientId)(clientId, "first"),
    size: _paginationTheme.paginationTheme.iconButton.size(themeMode),
    onClick: onFirstPageClick,
    "aria-label": i18n._( /* i18n */{
      comment: "This aria label is read to the user when the screen reader focuses on the first page button",
      id: "Go to the first page"
    }),
    isDisabled: cannotGoBack,
    iconBefore: icons.firstPage(themeMode),
    themeMode: themeMode
  })) : null, /*#__PURE__*/_react.default.createElement(_StyledIconButton.StyledIconButton, {
    appearance: _paginationTheme.paginationTheme.iconButton.appearance(themeMode),
    clientId: (0, _lodestarUtils.getClientId)(clientId, "prev"),
    size: "medium",
    onClick: onPrevClick,
    "aria-label": i18n._( /* i18n */{
      comment: "This aria label is read to the user when the screen reader focuses on the previous button",
      id: "Show previous {entitiesPerPage} entities",
      values: {
        entitiesPerPage
      }
    }),
    isDisabled: cannotGoBack,
    iconBefore: icons.previousPage(themeMode),
    themeMode: themeMode
  }), /*#__PURE__*/_react.default.createElement(_StyledTypeRamp.StyledTypeRamp
  // @ts-expect-error TODO: update TypeRampV2 to accept themeMode directly
  , {
    system: themeMode.split("-")[0],
    themeMode: themeMode,
    variant: _paginationTheme.paginationTheme.text.variant(themeMode),
    "aria-hidden": true
  }, i18n._( /* i18n */{
    comment: "This is the current range of entities that the user is viewing out of a total number of entities",
    id: "{from}-{to} of {total}",
    values: {
      from,
      to,
      total
    }
  })), /*#__PURE__*/_react.default.createElement(_StyledIconButton.StyledIconButton, {
    appearance: _paginationTheme.paginationTheme.iconButton.appearance(themeMode),
    clientId: (0, _lodestarUtils.getClientId)(clientId, "next"),
    size: "medium",
    onClick: onNextClick,
    "aria-label": i18n._( /* i18n */{
      comment: "This aria label is read to the user when the screen reader focuses on the next button",
      id: "Show next {entitiesPerPage} entities",
      values: {
        entitiesPerPage
      }
    }),
    isDisabled: cannotGoForward,
    iconBefore: icons.nextPage(themeMode),
    themeMode: themeMode
  }), includeFirstAndLastPage ? /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_StyledIconButton.StyledIconButton, {
    appearance: _paginationTheme.paginationTheme.iconButton.appearance(themeMode),
    clientId: (0, _lodestarUtils.getClientId)(clientId, "last"),
    size: "medium",
    onClick: onLastPageClick,
    "aria-label": i18n._( /* i18n */{
      comment: "This aria label is read to the user when the screen reader focuses on the last page button",
      id: "Go to the last page"
    }),
    isDisabled: cannotGoForward,
    iconBefore: icons.lastPage(themeMode),
    themeMode: themeMode
  })) : null, /*#__PURE__*/_react.default.createElement(_liveregion.LiveRegion, getLiveRegionProps()));
});
exports.PaginationBase = PaginationBase;
PaginationBase.displayName = "PaginationBase";
//# sourceMappingURL=PaginationBase.js.map